import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Check, ChevronsUpDown, Plus, User, Building2, Loader2 } from 'lucide-react';
import { cn } from '../../lib/cn';
import AddressBookModal from '../address-book/AddressBookModal';
import toast from 'react-hot-toast';

const normalizeSearchText = (value) => String(value || '')
  .toLowerCase()
  .replace(/[^a-z0-9]+/g, ' ')
  .trim();

const ContactCombobox = ({ 
  value, 
  onSelect, 
  label, 
  error, 
  placeholder = "Select contact...",
  disabled = false,
  className 
}) => {
  const [open, setOpen] = useState(false);
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [query, setQuery] = useState('');
  const [menuStyle, setMenuStyle] = useState(null);
  const selectionGuardRef = useRef(false);
  const wrapperRef = useRef(null);
  const triggerRef = useRef(null);
  const menuRef = useRef(null);
  const inputRef = useRef(null);

  const fetchContacts = async () => {
    setLoading(true);
    try {
      const response = await fetch('/api/address-book?includeUsers=1', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      const data = await response.json();
      if (!response.ok || !data?.success) {
        throw new Error(data?.message || 'Failed to load contacts');
      }
      setContacts(Array.isArray(data.data) ? data.data : []);
    } catch (error) {
      console.error('Error fetching contacts:', error);
      toast.error(error?.message || 'Failed to load contacts');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchContacts();
  }, []);

  useEffect(() => {
    if (open) selectionGuardRef.current = false;
  }, [open]);

  useEffect(() => {
    if (!open) return;

    const onMouseDown = (event) => {
      const target = event.target;
      if (triggerRef.current?.contains(target)) return;
      if (menuRef.current?.contains(target)) return;
      setOpen(false);
    };

    document.addEventListener('mousedown', onMouseDown);
    return () => document.removeEventListener('mousedown', onMouseDown);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const t = setTimeout(() => inputRef.current?.focus(), 0);
    return () => clearTimeout(t);
  }, [open]);

  useEffect(() => {
    if (!open) return;

    const updateMenuPosition = () => {
      const el = triggerRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const viewportHeight = window.innerHeight || 0;
      const sidePadding = 8;
      const gap = 8;
      const estimatedMenuHeight = 360;

      const spaceBelow = viewportHeight - rect.bottom;
      const openUp = spaceBelow < estimatedMenuHeight && rect.top > spaceBelow;
      const top = openUp ? Math.max(sidePadding, rect.top - gap) : rect.bottom + gap;
      const maxHeight = openUp
        ? Math.max(160, rect.top - sidePadding - gap)
        : Math.max(160, viewportHeight - (rect.bottom + gap) - sidePadding);

      setMenuStyle({
        position: 'fixed',
        left: rect.left,
        top: openUp ? undefined : top,
        bottom: openUp ? (viewportHeight - rect.top + gap) : undefined,
        width: rect.width,
        zIndex: 9999,
        maxHeight,
      });
    };

    updateMenuPosition();

    window.addEventListener('resize', updateMenuPosition);
    window.addEventListener('scroll', updateMenuPosition, true);
    return () => {
      window.removeEventListener('resize', updateMenuPosition);
      window.removeEventListener('scroll', updateMenuPosition, true);
    };
  }, [open]);

  const selectWithMouse = (e, contact) => {
    e.preventDefault();
    e.stopPropagation();
    if (selectionGuardRef.current) return;
    selectionGuardRef.current = true;
    handleSelect(contact);
  };

  const handleSelect = (contact) => {
    onSelect(contact);
    setOpen(false);
    setQuery('');
  };

  const handleContactAdded = (newContact) => {
    if (newContact) {
      setContacts(prev => [...prev, newContact]);
      handleSelect(newContact);
    }
    fetchContacts();
  };
  
  const getDisplayText = (contact) => {
    if (!contact) return '';
    return contact.displayName || contact.organizationName || contact.contactPerson || contact.email;
  };

  const isContactSelected = (contact) => {
    if (!contact) return false;
    if (value === undefined || value === null || value === '') return false;

    const valueStr = String(value);
    if (contact.id !== undefined && contact.id !== null && String(contact.id) === valueStr) return true;
    if (contact.organizationName && contact.organizationName === value) return true;
    if (contact.contactPerson && contact.contactPerson === value) return true;
    if (contact.email && contact.email === value) return true;
    if (getDisplayText(contact) === value) return true;
    return false;
  };

  const selectedContact = contacts.find(isContactSelected);
  const filteredContacts = contacts.filter((contact) => {
    const searchTerms = normalizeSearchText(query).split(' ').filter(Boolean);
    if (searchTerms.length === 0) return true;
    const haystack = normalizeSearchText([
      contact.organizationName,
      contact.contactPerson,
      contact.email,
      contact.phoneNumber,
      contact.designation,
      contact.address,
      contact.category,
      contact.displayName,
      contact.linkedUserName,
      contact.linkedUserEmail,
      contact.linkedUserDesignation,
      contact.linkedUserRole,
      contact.sourceId,
      contact.linkedUserId,
    ]
      .filter(Boolean)
      .join(' '));

    return searchTerms.every((term) => haystack.includes(term));
  });

  return (
    <div ref={wrapperRef} className={cn("relative w-full", className)}>
      {label && (
        <label className="block text-sm font-medium text-gray-700 mb-1">
          {label}
        </label>
      )}
      
      <button
        ref={triggerRef}
        type="button"
        role="combobox"
        aria-expanded={open}
        aria-haspopup="listbox"
        className={cn(
          "flex min-h-10 h-auto w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50",
          error ? "border-red-500" : "border-slate-200",
          disabled && "bg-slate-100 text-slate-500"
        )}
        disabled={disabled}
        onMouseDown={(e) => {
          if (disabled) return;
          e.preventDefault();
          setOpen((v) => !v);
        }}
      >
        <span className="flex items-center gap-2 flex-wrap text-left">
          {selectedContact ? (
            <>
              {selectedContact.sourceType === 'user' || selectedContact.category !== 'Government' ? <User className="w-4 h-4 text-green-500" /> : <Building2 className="w-4 h-4 text-blue-500" />}
              {getDisplayText(selectedContact)}
            </>
          ) : (
            <span className="text-gray-400">{placeholder}</span>
          )}
        </span>
        <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
      </button>

      {open && createPortal((
        <div
          ref={menuRef}
          style={menuStyle || { position: 'fixed', zIndex: 9999, left: 0, top: 0, width: 320 }}
          className="rounded-lg border border-gray-200 bg-white p-0 shadow-xl outline-none"
          role="listbox"
          onMouseDown={(e) => e.preventDefault()}
        >
          <div className="flex items-center border-b px-3">
            <input
              ref={inputRef}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search contacts..."
              className="flex h-11 w-full rounded-md bg-transparent py-3 text-sm outline-none placeholder:text-muted-foreground"
            />
          </div>

          <div
            className="overflow-y-auto p-1"
            style={{ maxHeight: Math.max(120, (menuStyle?.maxHeight || 300) - 96) }}
          >
            {loading ? (
              <div className="py-6 text-center text-sm text-gray-500">
                <span className="inline-flex items-center gap-2">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Loading contacts...
                </span>
              </div>
            ) : filteredContacts.length === 0 ? (
              <div className="py-6 text-center text-sm text-gray-500">No contact found.</div>
            ) : (
              <>
                <div className="px-2 py-1.5 text-xs font-medium text-gray-500">Contacts</div>
                {filteredContacts.map((contact) => (
                  <button
                    key={contact.id}
                    type="button"
                    className={cn(
                      "w-full text-left relative flex cursor-pointer select-none items-center rounded-md px-2 py-2 text-sm outline-none transition-colors hover:bg-gray-100",
                      isContactSelected(contact) && "bg-indigo-50 text-indigo-700"
                    )}
                    onMouseDown={(e) => selectWithMouse(e, contact)}
                    onClick={(e) => selectWithMouse(e, contact)}
                  >
                    <div className="flex flex-col w-full min-w-0">
                      <div className="flex items-center justify-between gap-4">
                        <span className="font-medium flex items-center gap-2 break-words text-left">
                          {contact.sourceType === 'user' || contact.category !== 'Government' ? <User className="w-3 h-3 text-green-500 shrink-0" /> : <Building2 className="w-3 h-3 text-blue-500 shrink-0" />}
                          {getDisplayText(contact)}
                        </span>
                        {contact.designation && (
                          <span className="text-xs text-gray-500 break-words text-right shrink-0">{contact.designation}</span>
                        )}
                      </div>
                      {contact.address && (
                        <span className="text-xs text-gray-400 ml-5 break-words text-left">{contact.address}</span>
                      )}
                    </div>
                    {isContactSelected(contact) && (
                      <Check className="ml-auto h-4 w-4 shrink-0 pl-2" />
                    )}
                  </button>
                ))}
              </>
            )}
          </div>

          <div className="border-t p-1 bg-gray-50">
            <button
              type="button"
              onMouseDown={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setOpen(false);
                setShowAddModal(true);
              }}
              className="flex w-full items-center justify-center gap-2 rounded-md bg-blue-600 px-3 py-2 text-sm font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
            >
              <Plus className="h-4 w-4" />
              Add New Contact
            </button>
          </div>
        </div>
      ), document.body)}

      {showAddModal && (
        <AddressBookModal
          isOpen={showAddModal}
          onClose={() => setShowAddModal(false)}
          onSuccess={handleContactAdded}
        />
      )}
    </div>
  );
};

export default ContactCombobox;
